import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { pick, prop } from 'ramda';
import { log } from 'log';
import { useTranslations } from 'js/hooks/i18n';
import { usePrevious } from 'js/hooks/usePrevious';
import { useIsMounted } from 'js/hooks/useIsMounted';
import { useInitiateDocumentUpload } from 'js/hooks/socket/hooks';
import { useUserSelector, useI18nSelector, useAppSelector } from 'js/store/hooks';
import { useGetPlayerTags } from 'server';

import * as helpers from '../helpers';
import { TAG_NAME } from '../constants';
import * as actions from '../store/actions';

export const useJumioVerifyPopup = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const isMounted = useIsMounted();
    const [tagValue, setTagValue] = useState('');
    const [isJumioShown, setIsJumioShown] = useState(false);
    const [isLoadingPlayerTag, setIsLoadingPlayerTag] = useState(false);
    const translations = useI18nSelector(prop('translations'));
    const { isLoggedIn } = useUserSelector(pick(['isLoggedIn']));
    const prevIsLoggedIn = usePrevious(isLoggedIn);
    const translate = useTranslations('playtech.mobile.system.jumio.');
    const dynamicTags = useAppSelector(prop('dynamicTags'));
    const getPlayerTags = useGetPlayerTags();
    const hasTranslationsLoaded = Object.keys(translations).length > 0;
    const initiateDocumentUpload = useInitiateDocumentUpload();
    const onAccept = useCallback(() => {
        const { handleJumioVerifyClick } = window.Playtech.API;

        if (typeof handleJumioVerifyClick === 'function') {
            handleJumioVerifyClick();

            return;
        }

        (async () => {
            try {
                const { redirectURL } = await initiateDocumentUpload({
                    service: 'netverify',
                    successUrl: helpers.getRedirectUrl('success'),
                    errorUrl: helpers.getRedirectUrl('error'),
                    errorLevel: 0
                });

                helpers.handleRedirect(redirectURL);
                // eslint-disable-next-line no-empty
            } catch {}
        })();
    }, [initiateDocumentUpload]);

    const showJumioVerifyPopup = useCallback(popupData => dispatch(actions.showJumioVerifyPopupAction(popupData)), [dispatch]);

    useEffect(() => {
        if (dynamicTags.DTAG_DISABLE_JUMIO_VERIFY_POPUP) {
            return;
        }

        (async () => {
            try {
                if (!isLoggedIn) {
                    return;
                }

                setIsLoadingPlayerTag(true);

                const { tags } = await getPlayerTags({ tagName: TAG_NAME });
                const value = tags?.find((tag) => tag.name === TAG_NAME)?.value;

                if (isMounted()) {
                    value && setTagValue(value);
                    setIsLoadingPlayerTag(false);
                }
            } catch (error) {
                log('error', error);
            }
        })();
    }, [dynamicTags, getPlayerTags, isMounted, isLoggedIn, setIsLoadingPlayerTag, setTagValue]);

    useEffect(() => {
        if (dynamicTags.DTAG_DISABLE_JUMIO_VERIFY_POPUP) {
            return;
        }

        if (!hasTranslationsLoaded || isLoadingPlayerTag) {
            return;
        }

        const params = new URLSearchParams(location.search);
        const isHideKYC = params.get('hideKYC') === '1';

        if (isHideKYC || !isLoggedIn || tagValue !== 'mandatory' || isJumioShown) {
            return;
        }

        const content = translate('content');

        setIsJumioShown(true);
        showJumioVerifyPopup({ content, onAccept });
    }, [
        location,
        tagValue,
        onAccept,
        translate,
        isLoggedIn,
        dynamicTags,
        isJumioShown,
        hasTranslationsLoaded,
        showJumioVerifyPopup,
        isLoadingPlayerTag
    ]);

    useEffect(() => {
        if (!prevIsLoggedIn || isLoggedIn) {
            return;
        }

        setIsJumioShown(false);
        setTagValue('');
    }, [prevIsLoggedIn, isLoggedIn, setTagValue, setIsJumioShown]);
};
